import { Search, X, TrendingUp, Tag, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState, useRef, useEffect } from 'react';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { useSearchSuggestions, usePopularSearches } from '@/hooks/useSearch';
import { getSearchResultUrl } from '@/hooks/useSearch';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  placeholder: string;
  language: string;
  className?: string;
  showSuggestions?: boolean;
  onSearch?: (query: string) => void;
}

const SearchBar = ({ 
  placeholder, 
  language, 
  className = "", 
  showSuggestions = true,
  onSearch 
}: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const { navigateWithCity, buildCityPath } = useCityNavigation();
  const searchRef = useRef<HTMLDivElement>(null);

  // Get search suggestions and popular searches
  const { data: suggestions = [], isLoading: suggestionsLoading } = useSearchSuggestions(query, language as 'en' | 'ar');
  const { data: popularSearches = [] } = usePopularSearches(language as 'en' | 'ar');

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show suggestions when query changes
  useEffect(() => {
    if (query.length >= 2 && showSuggestions) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [query, showSuggestions]);

  const forbiddenPattern = /[^a-zA-Z0-9\s]/;

  const handleSearch = async (searchQuery: string = query) => {
    if (forbiddenPattern.test(searchQuery)) {
      setError('Special characters are not allowed in search. Only letters, numbers, and spaces are allowed.');
      return;
    }
    setError('');
    if (!searchQuery.trim()) return;

    setIsOpen(false);
    
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      // Navigate to search results page
      navigateWithCity(`/search-results?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    setQuery(suggestion.name_en || suggestion.name_ar);
    setIsOpen(false);
    
    if (suggestion.slug) {
      // Navigate to specific page
      const url = getSearchResultUrl(suggestion);
      navigateWithCity(url);
    } else {
      // Search for the suggestion
      handleSearch(suggestion.name_en || suggestion.name_ar);
    }
  };

  const handlePopularSearchClick = (popularSearch: string) => {
    setQuery(popularSearch);
    handleSearch(popularSearch);
  };

  const clearQuery = () => {
    setQuery('');
    setError('');
    setIsOpen(false);
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'product':
        return <Gift className="h-4 w-4" />;
      case 'category':
        return <Tag className="h-4 w-4" />;
      case 'occasion':
        return <Gift className="h-4 w-4" />;
      default:
        return <Search className="h-4 w-4" />;
    }
  };

  const getSuggestionLabel = (type: string) => {
    switch (type) {
      case 'product':
        return language === 'ar' ? 'منتج' : 'Product';
      case 'category':
        return language === 'ar' ? 'فئة' : 'Category';
      case 'occasion':
        return language === 'ar' ? 'مناسبة' : 'Occasion';
      default:
        return '';
    }
  };

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
          <Input
            placeholder={placeholder}
            className={cn(
              "pl-12 pr-12 h-12 rounded-full border-border/30 bg-accent/30 focus:bg-background focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all",
              language === 'ar' && "text-right"
            )}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
          />
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              onClick={clearQuery}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </form>

      {/* Error message */}
      {error && (
        <div className="absolute left-0 top-full mt-1 text-xs text-destructive bg-background px-2 py-1 rounded shadow z-50">
          {error}
        </div>
      )}

      {/* Search suggestions dropdown */}
      {isOpen && showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="p-2">
              <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                {language === 'ar' ? 'الاقتراحات' : 'Suggestions'}
              </div>
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.type}-${suggestion.id}-${index}`}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className="text-muted-foreground">
                    {getSuggestionIcon(suggestion.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {language === 'ar' ? suggestion.name_ar : suggestion.name_en}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {getSuggestionLabel(suggestion.type)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Popular searches */}
          {popularSearches.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="text-xs font-medium text-muted-foreground px-2 py-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {language === 'ar' ? 'البحث الشائع' : 'Popular Searches'}
              </div>
              <div className="flex flex-wrap gap-1">
                {popularSearches.slice(0, 6).map((search, index) => (
                  <button
                    key={index}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    onClick={() => handlePopularSearchClick(search)}
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading state */}
          {suggestionsLoading && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {language === 'ar' ? 'جاري البحث...' : 'Searching...'}
            </div>
          )}

          {/* No suggestions */}
          {!suggestionsLoading && suggestions.length === 0 && query.length >= 2 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {language === 'ar' ? 'لا توجد اقتراحات' : 'No suggestions found'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;